import React, { FC, useMemo, useState, ReactElement, Fragment, ChangeEvent, useCallback, useContext } from 'react';
import clsx from 'clsx';
import { CheckboxInput, Tooltip } from '@td-client-frontend/common';
import { BaseProfileIcon, HamburgerIcon, PencilIcon, RoadSignsIcon, TrashIcon } from '@td-client-frontend/icons';
import { Popover } from '@headlessui/react';
import { useAuth } from '../../../../context/AuthContext';
import WithPermission, { PermMode } from '../../../WithPermission';
import { MarketingComments } from './MarketingComments/MarketingComments';
import { PreferencesForm } from './Preferences';
import { RegistrationAndRetention } from './RegistrationAndRetention/RegistrationAndRetention';
import {
  TabType,
  MarketingProfileById,
  MarketingHierarchyRowData,
  RegAndRetentionData
} from '../../../../@types/Marketing';
import { EditNameModal } from '../../components/EditNameModal';
import { ChangeAssignedProfileModal } from '../../components/ChangeAssignedProfileModal';
import DeleteProfileModal from '../../components/DeleteProfileModal';
import { Nullable } from '../../../../@types/common';
import { PendingChangesContext } from '../../../../context/PendingChangesContext';
import { Permissions } from '../../../../@types/Permissions';
import { TabSettings, TabsWithPerms } from '../../../TabsWithPerms';
interface ActionItemProps {
  onClick: () => void;
  label: string;
  icon: ReactElement;
  className?: string;
}

interface ProfileProps {
  profileData: MarketingProfileById;
  groupOrgData?: Nullable<MarketingHierarchyRowData>;
  isReadOnly?: boolean;
  onValid?: (data: RegAndRetentionData) => void;
  onInvalid?: () => void;
  onChange: (data: MarketingProfileById, isChanged?: boolean) => void;
  onDelete?: () => void;
}

const marketingProfileWritePermission = [
  Permissions.MarketingProfileRetentionWrite,
  Permissions.MarketingProfileRegistrationWrite,
  Permissions.MarketingProfilePreferencesWrite
];

const ActionItem: FC<ActionItemProps> = ({ onClick, label, icon, className }) => (
  <button onClick={onClick} className={clsx('w-full flex items-center border-b-1 px-4 py-3', className)}>
    <div className="mr-2">{icon}</div>
    <div className="text-black text-l leading-5">{label}</div>
  </button>
);

export const Profile: FC<ProfileProps> = ({
  profileData,
  groupOrgData = null,
  isReadOnly = false,
  onValid,
  onInvalid,
  onChange,
  onDelete
}) => {
  const [showChangeNameModal, setShowChangeNameModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showChangeAssignedProfileModal, setShowChangeAssignedProfileModal] = useState<boolean>(false);

  const { checkPerm } = useAuth();

  const openDeleteModal = () => setShowDeleteModal(true);
  const openChangeNameModal = () => setShowChangeNameModal(true);

  const onChangeAssignedProfileOpen = () => setShowChangeAssignedProfileModal(true);

  const { isChanged } = useContext(PendingChangesContext);

  const handleBaseProfileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange({
        ...profileData,
        default: event.target.checked
      });
    },
    [onChange, profileData]
  );

  const handleProfileNameChange = useCallback(
    (name) => {
      onChange({
        ...profileData,
        name
      });
    },
    [onChange, profileData]
  );

  const handleSettingsChange = useCallback(
    (data) => {
      onChange({
        ...profileData,
        settings: {
          ...profileData.settings,
          ...data
        }
      });
    },
    [onChange, profileData]
  );

  const handleTotalCommentsChange = useCallback(
    (totalComments) => {
      onChange(
        {
          ...profileData,
          totalComments
        },
        isChanged
      );
    },
    [isChanged, onChange, profileData]
  );

  const showActions: boolean = useMemo(() => {
    const hasOne = checkPerm(marketingProfileWritePermission, PermMode.ANY);
    const hasDeletePerms = checkPerm([Permissions.MarketingProfileDeleteWrite]);
    return isReadOnly ? false : hasOne || (hasDeletePerms && !groupOrgData);
  }, [checkPerm, groupOrgData, isReadOnly]);

  const tabsSettings: TabSettings = useMemo(
    () => ({
      [TabType.RegRetention]: {
        perms: [Permissions.MarketingProfileRetentionRead, Permissions.MarketingProfileRegistrationRead],
        permsMode: PermMode.ANY,
        label: 'Registration and Retention',
        component: (
          <RegistrationAndRetention
            profileData={profileData}
            onChange={handleSettingsChange}
            isReadOnly={isReadOnly}
            onValid={onValid}
            onInvalid={onInvalid}
          />
        )
      },
      [TabType.Preferences]: {
        perms: [Permissions.MarketingProfilePreferencesRead],
        permsMode: PermMode.ALL,
        label: 'Preferences',
        component: <PreferencesForm profileData={profileData} onChange={handleSettingsChange} isReadOnly={isReadOnly} />
      },
      [TabType.Comments]: {
        perms: [Permissions.MarketingProfileCommentsRead],
        permsMode: PermMode.ALL,
        label: `Comments ${profileData.totalComments !== 0 ? `(${profileData.totalComments})` : ''}`,
        component: <MarketingComments profileData={profileData} onTotalCommentsChange={handleTotalCommentsChange} />
      }
    }),
    [handleSettingsChange, handleTotalCommentsChange, isReadOnly, onInvalid, onValid, profileData]
  );

  return (
    <div>
      {showDeleteModal && (
        <DeleteProfileModal
          profileData={profileData}
          isOpen={showDeleteModal}
          setOpen={setShowDeleteModal}
          onDelete={onDelete}
        />
      )}
      {showChangeNameModal && (
        <EditNameModal
          onProfileNameChange={handleProfileNameChange}
          isOpen={showChangeNameModal}
          setOpen={setShowChangeNameModal}
        />
      )}
      {showChangeAssignedProfileModal && (
        <ChangeAssignedProfileModal
          profileData={profileData}
          groupOrgData={groupOrgData}
          isOpen={showChangeAssignedProfileModal}
          setOpen={setShowChangeAssignedProfileModal}
        />
      )}
      {groupOrgData?.name && (
        <div className="mb-4">
          <div className="text-lg text-gray-60 leading-7">
            Org/Group:{' '}
            <span className="text-black ml-1" data-testid="orgGroupName">
              {groupOrgData?.name}
            </span>
          </div>
          {groupOrgData.group?.legacyGroupId && (
            <div className="text-lg text-gray-60 leading-7">
              ID:{' '}
              <span className="text-black ml-1" data-testid="orgGroupId">
                {groupOrgData.group.legacyGroupId}
              </span>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center text-lg text-gray-60 leading-7 pb-7 border-b-1">
        <div>
          <span>Profile:</span>
          <span className="text-black text-2xl ml-2 leading-8" data-testid="profileName">
            {profileData.name}
          </span>
        </div>

        {profileData.default && (
          <div className="ml-4">
            <Tooltip id="baseProfile" type="dark" border={false} place="top" visible />
            <BaseProfileIcon
              data-testid="baseProfileIcon"
              data-tip="Base profile"
              data-for="baseProfile"
              className="cursor-pointer"
            />
          </div>
        )}
        <div className="flex items-center ml-auto">
          {!isReadOnly && (
            <WithPermission requiredPerms={[Permissions.MarketingProfileBaseCreate]}>
              <>
                <CheckboxInput colorType="blue" checked={profileData.default} onChange={handleBaseProfileChange} />
                <span className="text-black text-l ml-2 leading-5">Set as a base profile</span>
              </>
            </WithPermission>
          )}
          {showActions && (
            <Popover className="ml-9 relative">
              {({ open }) => (
                <Fragment>
                  <Popover.Button className="flex items-center" data-testid="actionPopoverButton">
                    <HamburgerIcon />
                    <span className="text-xl ml-2 leading-7 text-primary-500">Actions</span>
                  </Popover.Button>
                  <Popover.Panel
                    data-testid="actionPopoverPanel"
                    className={clsx(
                      open ? 'block' : 'hidden',
                      'absolute right-0 w-72 py-1 mt-4 rounded-lg flex flex-col shadow-all-side justify-center bg-white overflow-hidden z-20'
                    )}
                  >
                    <WithPermission requiredPerms={marketingProfileWritePermission} mode={PermMode.ANY}>
                      <>
                        {groupOrgData && (
                          <ActionItem
                            label="Change assigned profile"
                            onClick={onChangeAssignedProfileOpen}
                            icon={<RoadSignsIcon />}
                          />
                        )}
                        <ActionItem
                          label="Edit profile name"
                          onClick={openChangeNameModal}
                          icon={<PencilIcon />}
                          className={clsx(groupOrgData && 'border-none')}
                        />
                      </>
                    </WithPermission>
                    {!groupOrgData && (
                      <WithPermission requiredPerms={[Permissions.MarketingProfileDeleteWrite]}>
                        <ActionItem
                          label="Delete profile"
                          onClick={openDeleteModal}
                          icon={<TrashIcon />}
                          className="border-none"
                        />
                      </WithPermission>
                    )}
                  </Popover.Panel>
                </Fragment>
              )}
            </Popover>
          )}
        </div>
      </div>
      <TabsWithPerms tabsSettings={tabsSettings} />
    </div>
  );
};
export default Profile;
